// Vistas de una coleccion: kanban (que campo agrupa las columnas, renombres
// y tonos), calendario (campos de fecha) y orden de las vistas. Una fila por
// coleccion; el nombre de la tabla viene de cuando solo existia el kanban.
// Ver docs/analisis-kanban-colecciones.md.

import { v } from "convex/values";
import { defineTableWithInternalControl, internalControl } from "../../lib/shared/validators";

export const kanbanColumnTone = v.union(
  v.literal("neutral"),
  v.literal("info"),
  v.literal("success"),
  v.literal("warning"),
  v.literal("destructive"),
);

/** Color de una columna, por valor de la opción. Lista y no objeto: el
 *  valor puede no ser ASCII ("Crítico") y Convex solo acepta claves ASCII. */
export const kanbanColumnToneEntry = v.object({ value: v.string(), tone: kanbanColumnTone });
/** Etiqueta visible de una columna, por valor de la opción. */
export const kanbanColumnLabelEntry = v.object({ value: v.string(), label: v.string() });

/** Vistas de una coleccion. La tabla existe salvo que se desactive; las
 *  demas, si hay config. */
export const collectionView = v.union(v.literal("table"), v.literal("kanban"), v.literal("calendar"));

/** Calendario: fecha de cada registro y, opcional, fecha de fin (rango). */
export const calendarSettings = v.object({
  /** Key del campo `date` que ubica el registro en el calendario, o una
   *  fecha de sistema (`_createdAt`, `_updatedAt`). */
  dateKey: v.string(),
  /** Key del campo `date` de fin; el registro ocupa todos los dias del rango. */
  endKey: v.optional(v.string()),
  /** Propiedades de la tarjeta (hasta 4 keys); sin valor: las primeras. */
  cardFields: v.optional(v.array(v.string())),
  /** La tarjeta muestra quien y cuando modifico el registro (como la
   *  columna "Modificado" de la tabla). */
  showModified: v.optional(v.boolean()),
});

/** Acciones predeterminadas que una vista puede ocultar. */
export const viewRecordAction = v.union(v.literal("create"), v.literal("update"), v.literal("delete"), v.literal("pdf"));

/** Vistas donde no se muestra cada acción predeterminada (solo la interfaz:
 *  el permiso no cambia). Sin valor: se muestra en todas. */
export const hiddenActions = v.object({
  create: v.optional(v.array(collectionView)),
  update: v.optional(v.array(collectionView)),
  delete: v.optional(v.array(collectionView)),
  pdf: v.optional(v.array(collectionView)),
});

export const validatorCollectionKanbanSettings = v.object({
  collectionId: v.id("collections"),
  /** Key del campo (`select` o `checkbox`) que agrupa las columnas. Sin
   *  valor: no hay kanban (la fila puede existir por el calendario). */
  groupByKey: v.optional(v.string()),
  /** Etiqueta visible por valor (no toca las opciones del campo). */
  columnLabelList: v.optional(v.array(kanbanColumnLabelEntry)),
  /** Tono por valor (token del tema, no hex). */
  columnToneList: v.optional(v.array(kanbanColumnToneEntry)),
  /** Formato viejo de `columnLabelList` (objeto valor → etiqueta). Se lee y
   *  se quita al guardar; ver migrations/migrate_kanban_column_lists.ts. */
  columnLabels: v.optional(v.record(v.string(), v.string())),
  /** Formato viejo de `columnToneList` (objeto valor → tono). Ídem. */
  columnTone: v.optional(v.record(v.string(), kanbanColumnTone)),
  /** Propiedades de la tarjeta (hasta 4 keys); sin valor: las primeras. */
  cardFields: v.optional(v.array(v.string())),
  /** Kanban: la tarjeta muestra quien y cuando modifico el registro. */
  cardShowModified: v.optional(v.boolean()),
  /** Kanban: la tarjeta muestra solo los valores, sin la etiqueta del campo. */
  cardHideLabels: v.optional(v.boolean()),
  /** Orden de las vistas; la primera es la que abre al entrar. Sin valor:
   *  tabla primero. */
  viewOrder: v.optional(v.array(collectionView)),
  /** Vista calendario. Sin valor: no hay calendario. */
  calendar: v.optional(calendarSettings),
  /** La tabla esta desactivada. Si no queda otra vista, se muestra igual. */
  tableDisabled: v.optional(v.boolean()),
  /** Acciones predeterminadas ocultas por vista. */
  hiddenActions: v.optional(hiddenActions),
  internalControl,
});

export const collection_kanban_settings = defineTableWithInternalControl(
  validatorCollectionKanbanSettings,
).index("by_company_and_collection", ["internalControl.companyId", "collectionId"]);

/**
 * Orden de vistas elegido por un usuario para una coleccion. Tiene
 * prioridad sobre `collection_kanban_settings.viewOrder` (el de la
 * coleccion); sin fila, se usa ese.
 */
export const validatorCollectionUserViewPrefs = v.object({
  userId: v.id("users"),
  collectionId: v.id("collections"),
  viewOrder: v.array(collectionView),
  internalControl,
});

export const collection_user_view_prefs = defineTableWithInternalControl(
  validatorCollectionUserViewPrefs,
).index("by_user_and_collection", ["userId", "collectionId"]);
